import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, Switch, TextField, Typography,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { adminApi } from "rsa/adminApi";

const EMPTY = {
  businessName: "", ownerName: "", phone: "", email: "",
  businessType: "", address: "", pinCode: "", gstin: "", pan: "",
  latitude: "", longitude: "", serviceRadiusKm: "15",
  is24x7: false, rsaEnabled: true, isCollisionCare: true,
};

export default function AddVendorDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setError("");
    // /rsa/service-types is the public catalog the mobile app uses — preselect all,
    // an admin-onboarded vendor should be dispatchable for everything unless narrowed.
    adminApi("GET", "/rsa/service-types")
      .then((types) => { setServiceTypes(types); setCapabilities(types.map((t) => t.id)); })
      .catch(() => setServiceTypes([]));
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setBool = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));
  const toggleCapability = (id) =>
    setCapabilities((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const useMyLocation = () => {
    if (!navigator.geolocation) { setError("Geolocation is not available in this browser"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setForm((f) => ({ ...f, latitude: coords.latitude.toFixed(6), longitude: coords.longitude.toFixed(6) }));
        setGeoBusy(false);
      },
      (err) => { setError(`Could not get location: ${err.message}`); setGeoBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const submit = async () => {
    setError("");
    if (!form.businessName.trim()) { setError("Business name is required"); return; }
    if (form.phone.replace(/\D/g, "").length < 10) { setError("A 10-digit phone number is required — it's the vendor's mobile app login"); return; }
    if ((form.latitude === "") !== (form.longitude === "")) { setError("Enter both latitude and longitude, or neither"); return; }
    setBusy(true);
    try {
      const vendor = await adminApi("POST", "/admin/vendors", { ...form, capabilities });
      onCreated(vendor);
      onClose();
    } catch (e) {
      setError(e.status === 401 ? "Session expired — sign in again." : e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add vendor</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required size="small" label="Business name" value={form.businessName} onChange={set("businessName")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Owner name" value={form.ownerName} onChange={set("ownerName")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required size="small" label="Phone (10-digit)" value={form.phone} onChange={set("phone")}
              helperText="Used for vendor OTP login on the mobile app" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Email" value={form.email} onChange={set("email")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Business type" value={form.businessType} onChange={set("businessType")} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="GSTIN" value={form.gstin} onChange={set("gstin")} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="PAN" value={form.pan} onChange={set("pan")} />
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField fullWidth size="small" label="Address" value={form.address} onChange={set("address")} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="PIN code" value={form.pinCode} onChange={set("pinCode")} />
          </Grid>

          <Grid item xs={12}><Divider><Typography variant="caption">Business location — dispatch matches nearby requests against it</Typography></Divider></Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Latitude" value={form.latitude} onChange={set("latitude")} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Longitude" value={form.longitude} onChange={set("longitude")} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button fullWidth variant="outlined" startIcon={<MyLocationIcon />} disabled={geoBusy} onClick={useMyLocation}>
              {geoBusy ? "Locating…" : "Use my location"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Service radius (km)" value={form.serviceRadiusKm} onChange={set("serviceRadiusKm")} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" height="100%">
              <FormControlLabel control={<Switch checked={form.rsaEnabled} onChange={setBool("rsaEnabled")} />} label="RSA enabled" />
              <FormControlLabel control={<Switch checked={form.isCollisionCare} onChange={setBool("isCollisionCare")} />} label="Collision Care" />
              <FormControlLabel control={<Switch checked={form.is24x7} onChange={setBool("is24x7")} />} label="24×7" />
            </Box>
          </Grid>

          <Grid item xs={12}><Divider><Typography variant="caption">Service capabilities</Typography></Divider></Grid>
          <Grid item xs={12}>
            {serviceTypes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No service types available</Typography>
            ) : (
              <Box display="flex" gap={1} flexWrap="wrap">
                {serviceTypes.map((t) => (
                  <Chip
                    key={t.id}
                    label={t.name}
                    clickable
                    color={capabilities.includes(t.id) ? "primary" : "default"}
                    variant={capabilities.includes(t.id) ? "filled" : "outlined"}
                    onClick={() => toggleCapability(t.id)}
                    icon={<Checkbox size="small" checked={capabilities.includes(t.id)} sx={{ p: 0, pl: 0.5 }} />}
                  />
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? "Creating…" : "Create vendor"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
